import React, { useState } from "react";
import {
    Box,
    Typography,
    Stack,
    Container,
    Tabs,
    Tab,
    Paper,
    useTheme
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { getAllLegalDocuments } from "@/constants/legal";
import { LegalDocument } from "@/types/legal";

const LegalDocumentsPage: React.FC = () => {
    const { type } = useParams<{ type: "terms" | "privacy" }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const [tabValue, setTabValue] = useState<"terms" | "privacy">(type || "terms");

    const legalDocs = getAllLegalDocuments();
    const currentDoc: LegalDocument = legalDocs[tabValue];

    const handleTabChange = (
        _: React.SyntheticEvent,
        newValue: "terms" | "privacy"
    ) => {
        setTabValue(newValue);
        navigate(
            `/legal/${newValue}`
        );
    };


    const renderContent = () => (
        <Box
            sx={{
                "& h1": { fontSize: "2rem", fontWeight: 700, mb: 3, fontFamily: theme.typography?.fontFamily, color: theme.palette.text.primary },
                "& h2": { fontSize: "1.5rem", fontWeight: 600, mt: 4, mb: 2, fontFamily: theme.typography?.fontFamily, color: theme.palette.text.primary },
                "& h3": { fontSize: "1.2rem", fontWeight: 600, mt: 3, mb: 1.5, fontFamily: theme.typography?.fontFamily, color: theme.palette.primary.main },
                "& p": { fontSize: "1rem", lineHeight: 1.8, color: theme.palette.text.secondary, mb: 2, textAlign: "justify", fontFamily: theme.typography?.fontFamily },
                "& ul, & ol": { pl: 3, mb: 2, fontSize: "1rem", lineHeight: 1.8, color: theme.palette.text.secondary, fontFamily: theme.typography?.fontFamily },
                "& li": { mb: 1, fontFamily: theme.typography?.fontFamily },
                "& strong": { fontWeight: 600, color: theme.palette.text.primary, fontFamily: theme.typography?.fontFamily },
                "& a": { color: theme.palette.primary.main, textDecoration: "underline", fontFamily: theme.typography?.fontFamily },
            }}
            dangerouslySetInnerHTML={{ __html: currentDoc?.content || "" }}
        />
    );

    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 6 } }}>
                <Stack spacing={4}>
                    {/* Header */}
                    <Stack spacing={1}>
                        <Typography variant="h1">
                            {tabValue === "terms" ? "Términos y Condiciones" : "Política de Privacidad"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Versión {currentDoc?.version} • Última actualización:{" "}
                            {new Date(currentDoc?.publishedAt).toLocaleDateString("es-ES", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                            })}
                        </Typography>
                    </Stack>

                    {/* Tabs */}
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            "& .MuiTab-root": {
                                fontFamily: "Fredoka",
                                fontWeight: 600,
                                textTransform: "none",
                                fontSize: "1rem",
                            }
                        }}
                    >
                        <Tab label="Términos y Condiciones" value="terms" />
                        <Tab label="Política de Privacidad" value="privacy" />
                    </Tabs>

                    {/* Content */}
                    <Box>{renderContent()}</Box>

                    {/* Footer */}
                    <Box
                        sx={{
                            pt: 4,
                            mt: 4,
                            borderTop: 1,
                            borderColor: "divider",
                            textAlign: "center"
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            ¿Tienes preguntas? Contáctanos a{" "}
                            <a href="mailto:info@bitelink.app">info@bitelink.app</a>
                        </Typography>
                    </Box>
                </Stack>
            </Paper>
        </Container>
    );
};

export default LegalDocumentsPage;